/**
 * House-rule configuration. By default every optional house rule is off (all boxes unchecked);
 * a host turns on the ones their table plays.
 */
export interface RulesConfig {
  /** Points needed to win the game. */
  targetScore: number;
  /**
   * If true the dealer may not pass in the second round of bidding.
   * If false (default) and everyone passes twice, the hand is thrown in and the deal passes left.
   */
  stickTheDealer: boolean;
  /** A defender may choose to defend alone (their partner sits out). */
  allowDefendAlone: boolean;
  /** Points a lone defender's team scores for euchring the makers. */
  loneDefenderEuchrePoints: number;
  /**
   * "No ace, no face": a player dealt only 9s and 10s may call for a redeal
   * (same dealer deals again) before bidding starts.
   */
  noAceNoFaceRedeal: boolean;
  /**
   * Farmer's hand: a player dealt at least `farmersHandMinLowCards` nines/tens may,
   * before bidding, exchange exactly three of those nines/tens for the three
   * face-down cards of the kitty. Only one exchange per deal.
   */
  farmersHand: boolean;
  farmersHandMinLowCards: number;
}

export const DEFAULT_RULES: Readonly<RulesConfig> = Object.freeze({
  targetScore: 10,
  stickTheDealer: false,
  allowDefendAlone: false,
  loneDefenderEuchrePoints: 4,
  noAceNoFaceRedeal: false,
  farmersHand: false,
  farmersHandMinLowCards: 3,
});

/** Validate and normalize a partial rules object coming from a client. */
export function normalizeRules(input: unknown): RulesConfig {
  const r: RulesConfig = { ...DEFAULT_RULES };
  if (!input || typeof input !== 'object') return r;
  const o = input as Record<string, unknown>;
  if (typeof o.targetScore === 'number' && Number.isInteger(o.targetScore) && o.targetScore >= 1 && o.targetScore <= 50) {
    r.targetScore = o.targetScore;
  }
  for (const key of ['stickTheDealer', 'allowDefendAlone', 'noAceNoFaceRedeal', 'farmersHand'] as const) {
    if (typeof o[key] === 'boolean') r[key] = o[key] as boolean;
  }
  if (typeof o.loneDefenderEuchrePoints === 'number' && [2, 4].includes(o.loneDefenderEuchrePoints)) {
    r.loneDefenderEuchrePoints = o.loneDefenderEuchrePoints;
  }
  if (
    typeof o.farmersHandMinLowCards === 'number' &&
    Number.isInteger(o.farmersHandMinLowCards) &&
    o.farmersHandMinLowCards >= 3 &&
    o.farmersHandMinLowCards <= 5
  ) {
    r.farmersHandMinLowCards = o.farmersHandMinLowCards;
  }
  return r;
}

/** The values the table-setup screens offer for each numeric rule. */
export const RULE_CHOICES = Object.freeze({
  targetScore: [5, 7, 10, 11, 15, 21] as const,
  loneDefenderEuchrePoints: [2, 4] as const,
  farmersHandMinLowCards: [3, 4, 5] as const,
});

export function rulesEqual(a: RulesConfig, b: RulesConfig): boolean {
  return (Object.keys(DEFAULT_RULES) as (keyof RulesConfig)[]).every((k) => a[k] === b[k]);
}
